"use client"

import type { MembersBoardIntegration } from "@ibada/shared-types/overview"

import { HomeTile, HomeTileEmpty, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import { BoardPersonRow } from "@/features/members/components/board/board-person-row"
import { useLocale, useTranslations } from "@/i18n"

/** Three rows fit beside the coverage bar at the board's tile height. */
const MAX_ROWS = 3

/**
 * To integrate: active members in no group and no ministry, the people a members lead should place next. The bar
 * shows how much of the active membership already belongs somewhere; it is never drawn without active members.
 */
export function IntegrationTile({
  integration,
  groupsLink,
  profileHref,
}: {
  integration: MembersBoardIntegration
  groupsLink: HomeTileLink | null
  profileHref: ((memberId: string) => string) | null
}) {
  const t = useTranslations("people.members.overview.board.integration")
  const locale = useLocale()
  const dateFormat = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" })
  const { activeTotal, withoutGroup, items } = integration
  const integratedShare = activeTotal > 0 ? Math.round(((activeTotal - withoutGroup) / activeTotal) * 100) : 0

  return (
    <HomeTile
      title={t("title")}
      subtitle={
        activeTotal === 0
          ? t("subtitleNoActive")
          : withoutGroup === 0
            ? t("subtitleNone")
            : t(withoutGroup === 1 ? "subtitleOne" : "subtitleOther", { count: withoutGroup, total: activeTotal })
      }
      link={groupsLink}
      testId='members-board-integration'
    >
      {activeTotal === 0 ? (
        <HomeTileEmpty message={t("emptyNoActive")} />
      ) : withoutGroup === 0 ? (
        <HomeTileEmpty message={t("empty")} action={groupsLink} />
      ) : (
        <>
          <div className='flex flex-col gap-1'>
            <span className='flex text-[12.5px]'>
              <span className='text-muted-foreground'>{t("integrated")}</span>
              <span className='ml-auto font-bold tabular-nums'>{integratedShare}%</span>
            </span>
            <span className='flex h-2 overflow-hidden rounded-full bg-muted' aria-hidden>
              <span className='rounded-full bg-emerald-600 dark:bg-emerald-500' style={{ width: `${integratedShare}%` }} />
            </span>
          </div>
          <ul className='mt-1 flex flex-col gap-2.5'>
            {items.slice(0, MAX_ROWS).map((person) => (
              <BoardPersonRow
                key={person.id}
                testId='integration-row'
                person={person}
                profileHref={profileHref ? profileHref(person.id) : null}
                detail={t("memberSince", { date: dateFormat.format(new Date(person.createdAt)) })}
                showCall={false}
              />
            ))}
          </ul>
        </>
      )}
    </HomeTile>
  )
}
